import { Box, Stack, Typography } from "@mui/material";
import {
  Dialog,
  IconButton,
  Snackbar,
  SnackbarProps,
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { Copy } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBorder } from "@pulse/shared-components";
import { convertTimestampFormat } from "../../utils/DateUtils";
import { OneTimeAPICredentials } from "../stores/APIKeyManagementStore";

interface OneTimeCredentialsDialogProps {
  credentials: OneTimeAPICredentials;
  onDone: VoidFunction;
}

export const OneTimeCredentialsDialog: React.FC<
  OneTimeCredentialsDialogProps
> = ({ credentials, onDone }) => {
  const [copyFeedback, setCopyFeedback] =
    useState<Pick<SnackbarProps, "message" | "severity">>();
  const { t } = useTranslation();
  const spacing = useSpacing();
  const typography = useTypography();
  const tokens = useFoundationColorTokens();
  const radius = useCornerRadius();
  const border = useBorder();

  const copyValue = async (
    value: string,
    successMessage: string,
  ): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback({ message: successMessage, severity: "success" });
    } catch {
      setCopyFeedback({
        message: t("externalPublicApis.apiKeyManagementCopyUnexpectedError"),
        severity: "error",
      });
    }
  };

  const CredentialRow = ({
    label,
    value,
    copyLabel,
    copiedMessage,
  }: {
    label: string;
    value: string;
    copyLabel: string;
    copiedMessage: string;
  }): React.ReactElement => (
    <Stack gap={spacing.spaceXS}>
      <Typography {...typography.s2} color={tokens.labelSubtle}>
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={spacing.spaceSM}
        padding={`${spacing.spaceSM} ${spacing.spaceMD}`}
        border={border.default}
        borderRadius={radius.radiusXS}
        bgcolor={tokens.backgroundSubtle}
      >
        <Box
          component="code"
          color={tokens.label}
          sx={{ overflowWrap: "anywhere" }}
        >
          {value}
        </Box>
        <IconButton
          name={copyLabel}
          size="small"
          icon={<Copy />}
          variant="plain-neutral"
          onClick={() => void copyValue(value, copiedMessage)}
        />
      </Stack>
    </Stack>
  );

  return (
    <Dialog
      open
      title={t("externalPublicApis.apiKeyManagementOneTimeCredentialsTitle")}
      secondaryButtonText={t("common.done")}
      onSecondaryButtonClick={onDone}
      disableBackdropClick
      width="680px"
    >
      <>
        <Stack gap={spacing.spaceLG} width="100%">
          <Typography {...typography.b1} color={tokens.labelWarning}>
            {t("externalPublicApis.apiKeyManagementOneTimeCredentialsWarning")}
          </Typography>
          <CredentialRow
            label={t("externalPublicApis.apiKeyManagementApiKeyIdLabel")}
            value={credentials.apiKeyId}
            copyLabel="copyAPIKeyId"
            copiedMessage={t("externalPublicApis.apiKeyManagementCopiedKeyId")}
          />
          <CredentialRow
            label={t("externalPublicApis.apiKeyManagementApiSecretKeyLabel")}
            value={credentials.apiSecretKey}
            copyLabel="copyAPISecretKey"
            copiedMessage={t("externalPublicApis.apiKeyManagementCopiedSecret")}
          />
          <Stack gap={spacing.spaceXS}>
            <Typography {...typography.s2} color={tokens.labelSubtle}>
              {t("externalPublicApis.apiKeyManagementExpiryLabel")}
            </Typography>
            <Typography {...typography.b1} color={tokens.label}>
              {convertTimestampFormat(credentials.expiresAt)}
            </Typography>
          </Stack>
        </Stack>
        {copyFeedback && (
          <Snackbar
            isOpen
            isDismissible
            message={copyFeedback.message}
            severity={copyFeedback.severity}
            onClose={() => setCopyFeedback(undefined)}
          />
        )}
      </>
    </Dialog>
  );
};
